import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { isEmpty, isEmail } from "../../utils/formValidator";
import InputMask from "react-input-mask";
import debounce from "lodash/debounce";

import br from "../../resources/br/input.js";
import en from "../../resources/en/input.js";
import es from "../../resources/es/input.js";

const masks = {
  phone: "(99) 999999999",
  phoneWpp: "(99) 9 9999-9999",
  cnpj: "99.999.999/9999-99",
  cpf: "999.999.999-99",
  cep: "99999-999",
};

const Input = ({
  id,
  label,
  placeholder,
  type,
  aditionalClass,
  validationErrorMessage,
  readOnly,
  backEndvalidationError,
  callBackFunction,
  value,
  backgroundColor,
  mask,
  required,
  customValidation,
  language,
}) => {
  const textsInput = useMemo(() => ({ en, br, es }), []);
  const [inputValue, setInputValue] = useState(value || "");
  const [validationClass, setValidationClass] = useState("");
  const [validationError, setValidationError] = useState(false);

  const validate = useCallback(
    debounce((inputValue) => {
      let validationClass = "is-valid";
      let validationError = false;

      if (customValidation) {
        if (!customValidation(inputValue)) {
          validationClass = "is-invalid";
          validationError = true;
        }
      } else if (
        required &&
        (isEmpty(inputValue) || (type === "tel" && inputValue === "("))
      ) {
        validationClass = "is-invalid";
        validationError = true;
      } else if (type === "tel") {
        const digitsPhone = inputValue.replace(/\D/g, "");
        if (
          (digitsPhone.length === 10 && mask === "phoneWpp") ||
          (digitsPhone.length === 11 && digitsPhone[2] !== "9") ||
          digitsPhone.length < 10
        ) {
          validationClass = "is-invalid";
          validationError = true;
        }
      } else if (type === "email" && !isEmail(inputValue)) {
        validationClass = "is-invalid";
        validationError = true;
      }

      setValidationClass(validationClass);
      setValidationError(validationError);
      callBackFunction(id, !validationError, inputValue);
    }, 300),
    [customValidation, required, type, mask, callBackFunction, id]
  );

  useEffect(() => {
    if (value) {
      setInputValue(value);
      validate(value);
    }
    if (backEndvalidationError) {
      setValidationClass("is-invalid");
    }
  }, [value, backEndvalidationError, validate]);

  useEffect(() => {
    if (backEndvalidationError) {
      setValidationClass("is-invalid");
    }
  }, [backEndvalidationError]);

  const handleChange = (e) => {
    const { value } = e.target;
    setInputValue(value);
    validate(value);
  };

  // const handleBlur = () => {
  //   validate(inputValue);
  // };

  const readOnlyClass = readOnly ? "lev-input-readonly" : "";
  const translatedErrorMessage = language ? textsInput[language] : null;
  const displayErrorMessage = backEndvalidationError || validationError;

  return (
    <>
      {type === "textarea" ? (
        <textarea
          className={`form-control form-control-md ${aditionalClass} ${validationClass} ${readOnlyClass}`}
          id={id}
          placeholder={placeholder}
          rows={4}
          readOnly={readOnly}
          onChange={handleChange}
          // onBlur={handleBlur}
          value={inputValue}
        />
      ) : (
        <InputMask
          className={`form-control form-control-md ${aditionalClass} ${validationClass} ${readOnlyClass}`}
          style={{ background: backgroundColor, marginBottom: 5 }}
          id={id}
          required={required}
          placeholder={placeholder}
          type={type}
          readOnly={readOnly}
          onChange={handleChange}
          // onBlur={handleBlur}
          value={inputValue}
          mask={masks[mask]}
          maskChar=""
          alwaysShowMask={false}
        />
      )}
      <label
        className={`form-control-placeholder ${
          inputValue && "fixed-placeholder"
        }`}
        htmlFor={id}
        style={{ background: backgroundColor }}
      >
        {label}
      </label>
      {displayErrorMessage && (
        <div className="notranslate invalid-feedback" translate="no">
          {translatedErrorMessage
            ? translatedErrorMessage[validationErrorMessage]
            : validationErrorMessage}
        </div>
      )}
    </>
  );
};

Input.defaultProps = {
  requiredErrorMessage: "Campo obrigatório",
  validationErrorMessage: "Dado inválido",
  required: true,
  readOnly: false,
  placeholder: "",
  backgroundColor: "#fff",
  customValidation: null,
};

Input.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  type: PropTypes.oneOf(["text", "email", "password", "textarea", "tel"])
    .isRequired,
  aditionalClass: PropTypes.string,
  required: PropTypes.bool,
  requiredErrorMessage: PropTypes.string,
  validationErrorMessage: PropTypes.string,
  readOnly: PropTypes.bool,
  backEndvalidationError: PropTypes.bool,
  callBackFunction: PropTypes.func.isRequired,
  value: PropTypes.string,
  backgroundColor: PropTypes.string,
  customValidation: PropTypes.func,
  language: PropTypes.oneOf(["en", "br", "es"]),
};

export default Input;
